<template>
  <dialog ref="dialogRef" class="positions-dialog" @close="emit('close')" @click="handleBackdropClick">
    <div class="positions-card">
      <div class="positions-title">{{ localize('handshake.photo.positionTitle') }}</div>

      <div class="positions">
        <div v-for="position in choices" :key="position" class="position-row" @click="choose(position)">
          <button
            type="button"
            class="position-choice"
            :class="{ 'is-taken': isTaken(position), 'is-saving': isSaving(position) }"
            :aria-label="localize('handshake.photo.changePosition')"
            :aria-pressed="selected === position"
          >
            <span v-if="selected === position" class="choice-filled" />
            <span v-else class="choice-empty" />
          </button>
          <HandshakePositionMark :position-index="position" align="start" show-number />
        </div>
      </div>

      <div class="positions-close" @click="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import { gambits, localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'vue-toastification';

const props = defineProps<{
  handshakeId: string;
  /** Positions saved on this handshake. The photo can move among these. */
  positions: number[];
  /** The position this photo has now. Nothing, for a meeting photo. */
  current: number | null;
  /** Positions this photo's owner already has five other photos on. */
  taken?: number[];
  /** The meeting already has five other photos, so this one cannot join them. */
  meetingFull?: boolean;
  imageHash: string;
}>();

const emit = defineEmits<{ close: []; change: [positionIndex: number | null] }>();

const toast = useToast();
const dialogRef = ref<HTMLDialogElement>();
const selected = ref<number | null>(props.current);
const saving = ref(new Set<number>());
/** One move at a time, so a slower tap cannot put the photo back where a newer tap left it. */
let moveQueue: Promise<void> = Promise.resolve();

const choices = computed(() => {
  const list = [...props.positions];
  if (props.current != null && !list.includes(props.current)) list.push(props.current);
  return list;
});

const isTaken = (position: number): boolean => (props.taken ?? []).includes(position);

const isSaving = (position: number): boolean => saving.value.has(position);

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (event: MouseEvent) => {
  if (event.target === dialogRef.value) emit('close');
};

const choose = (position: number) => {
  if (saving.value.has(position)) return;

  const leaving = selected.value === position;
  if (leaving && props.meetingFull) {
    toast.error(localize('handshake.photo.limitHandshake'));
    return;
  }
  if (!leaving && isTaken(position)) {
    toast.error(localize('handshake.photo.taken'));
    return;
  }

  const next = leaving ? null : position;
  const previous = selected.value;
  selected.value = next;
  saving.value = new Set(saving.value).add(position);

  moveQueue = moveQueue
    .then(async () => {
      try {
        const result = await gambits.userService.moveHandshakeImage(props.handshakeId, props.imageHash, next);

        if (result.error || !result.data) {
          if (selected.value === next) selected.value = previous;
          toast.error(result.error?.message || localize('handshake.photo.moveFailed'));
          return;
        }

        emit('change', next);
      } finally {
        const still = new Set(saving.value);
        still.delete(position);
        saving.value = still;
      }
    })
    .catch(() => undefined);
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.positions-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 1rem var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.positions-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 420px);
  max-height: min(70vh, 640px);
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.positions-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

.positions {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-l);
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.position-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
}

.position-row :deep(.HandshakePositionMark) {
  flex: 1;
  min-width: 0;
  width: auto;
}

.position-choice {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 2rem;
  height: 2rem;
  margin-top: 4px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: none;
  color: inherit;
  cursor: pointer;

  &.is-saving::after {
    content: '';
    position: absolute;
    width: 26px;
    height: 26px;
    border: 2px solid var(--color-white-30);
    border-top-color: var(--color-white);
    border-radius: 50%;
    animation: choice-spin 600ms linear infinite;
  }

  &.is-taken {
    opacity: 0.35;
  }
}

.choice-empty,
.choice-filled {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid var(--color-white-30);
  box-sizing: border-box;
}

.choice-filled {
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-white);
  }
}

.positions-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

@keyframes choice-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
